import {
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react';
import { connect } from 'react-redux';
import {
    Box,
    Dialog,
    DialogActions,
    Grid,
    IconButton,
    Typography,
} from '@mui/material';
import {
    AppFormikButtonTiles,
    AppFormikTextField,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import { ManageAccessEditMemberScheme } from 'validators/ManageAccessScheme';
import { ManageAccessService } from 'services/ManageAccessService';
import { Toast, Utils } from 'shared/helpers';
import { typeMap } from './components/data/typeMap';
import { AxiosError } from 'axios';
import { useNetworkState } from 'react-use';

const ManageAccessEditDialog = (props: ManageAccessEditProps): ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { showForm, onCloseForm, onFinishForm, acccessState } = props;

    const [rolesData, setRolesData] = useState<roleData[] | null>(null);
    const [email, setEmail] = useState<string>('');
    const [status, setStatus] = useState<boolean[]>([]);
    const [roles] = useState<boolean[]>([]);
    const [rolesOption, setRoleOption] = useState<roleOption[]>([]);

    // UI Elements

    useLayoutEffect(() => {
        setStatus(acccessState.enabled ? [true] : [false, true]);
        setEmail(acccessState.email);

        // if (rolesData) {
        //     const roleIndex = rolesData.findIndex(
        //         (el) => el.roleId === acccessState.roleId
        //     );

        //     if (roleIndex > -1) {
        //         const roles = Array.from({ length: roleIndex + 1 }).map(
        //             (el, i) => roleIndex === i
        //         );
        //         setRoles(roles);
        //     }
        // }
    }, [acccessState, rolesData]);
    const [isRolesDisabled, setRolesDisabled] = useState<boolean>(false);
    useEffect(() => {
        ManageAccessService.getRoles()
            .then((roles) => {
                const rolesArray: roleData[] =
                    roles.data.data[0].paginatedResult;
                const rolesOptionArray: roleOption[] = [];
                rolesArray.forEach((el) => {
                    if (el.roleId === acccessState.roleId) {
                        rolesOptionArray.push({ value: typeMap[el.role][0] });
                    }
                });
                setRoleOption(rolesOptionArray);
                setRolesData(rolesArray);
            })
            .catch((error) => {
                setRolesDisabled(true);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, []);

    // const actionDeleteUser = useCallback(() => {
    //     ManageAccessService.deleteMember({ userId: acccessState.userId })
    //         .then((response) => {
    //             onFinishForm();
    //             Utils.handleSuccessResponse(response);
    //         })
    //         .catch(Utils.handleErrorResponse);
    // }, [acccessState.userId, onFinishForm]);

    const { online } = useNetworkState();
    const actionAccessOnSubmit = useCallback(
        (value: any) => {
            const initialRolesIndex = roles.findIndex(
                (el: boolean) => el === true
            );
            const rolesIndex = value.roles.findIndex(
                (el: boolean) => el === true
            );
            const initialStatusIndex = status.findIndex(
                (el: boolean) => el === true
            );
            const statusIndex = value.status.findIndex(
                (el: boolean) => el === true
            );
            if (
                initialRolesIndex !== rolesIndex &&
                statusIndex !== initialStatusIndex
            ) {
                Promise.all([
                    ManageAccessService.sendPatchToEnableMember({
                        userId: acccessState.userId,
                        enabled: statusIndex === 0,
                    }),
                    ManageAccessService.sendPatchMember({
                        userId: acccessState.userId,
                        roleId: rolesData ? rolesData[rolesIndex].roleId : '',
                    }),
                ])
                    .then((response) => {
                        onFinishForm();
                        response.forEach((resp) =>
                            Utils.handleSuccessResponse(resp)
                        );
                    })
                    .catch((error) => {
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            Toast.showErrorMessage(
                                'No internet connection, Please try again!'
                            );
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            } else if (initialRolesIndex !== rolesIndex) {
                ManageAccessService.sendPatchMember({
                    userId: acccessState.userId,
                    roleId: rolesData ? rolesData[rolesIndex].roleId : '',
                })
                    .then((response) => {
                        onFinishForm();
                        Utils.handleSuccessResponse(response);
                    })
                    .catch((error) => {
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            Toast.showErrorMessage(
                                'No internet connection, Please try again!'
                            );
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            } else if (statusIndex !== initialStatusIndex) {
                ManageAccessService.sendPatchToEnableMember({
                    userId: acccessState.userId,
                    enabled: statusIndex === 0,
                })
                    .then((response) => {
                        onFinishForm();
                        Utils.handleSuccessResponse(response);
                    })
                    .catch((error) => {
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            Toast.showErrorMessage(
                                'No internet connection, Please try again!'
                            );
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            } else if (online) {
                onFinishForm();
            } else {
                Toast.showErrorMessage(
                    'No internet connection, Please try again!'
                );
            }
        },
        [acccessState.userId, onFinishForm, online, roles, rolesData, status]
    );

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={showForm}
            className="edit-wrapper"
        >
            <Box className="header">
                <Typography variant="h3">Edit</Typography>
                <IconButton
                    edge="end"
                    onClick={onCloseForm}
                    className="app-icon-close"
                    title="close"
                >
                    <CloseIcon className="close-icon" />
                </IconButton>
            </Box>
            <Formik
                enableReinitialize
                initialValues={{ status, roles, email }}
                validateOnMount
                validationSchema={ManageAccessEditMemberScheme}
                onSubmit={actionAccessOnSubmit}
            >
                {({ handleSubmit }) => (
                    <>
                        <Grid className="content" container spacing={1}>
                            <Grid item xs={12} md={5}>
                                <AppFormikTextField
                                    name="email"
                                    placeholder="Type here"
                                    className="email-access-input"
                                    outerLabel
                                    disabled
                                    label="Email"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <AppFormikButtonTiles
                                    variant="outlined"
                                    name="status"
                                    size="small"
                                    label="Status"
                                    disabled={isRolesDisabled}
                                    multichoice={false}
                                    className="button-tiles-status"
                                    buttons={[
                                        { value: 'Enable' },
                                        { value: 'Disable' },
                                    ]}
                                    outerLabel
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <AppFormikButtonTiles
                                    variant="outlined"
                                    size="small"
                                    name="roles"
                                    multichoice={false}
                                    disabled={isRolesDisabled}
                                    className="button-tiles-roles"
                                    label="Role"
                                    buttons={Array.from(rolesOption).map(
                                        (e) => ({
                                            ...e,
                                            disabled: true,
                                        })
                                    )}
                                    outerLabel
                                />
                            </Grid>
                        </Grid>

                        <DialogActions className="px-6">
                            {/* <AppPrimaryButton
                                className="btn-small mb-4 delete-access"
                                title="Delete user"
                                onClick={actionDeleteUser}
                                loadingSize={22}
                                loadingText="PROCESSING"
                            /> */}
                            <AppPrimaryButton
                                className="btn-small mb-4 update-access"
                                title="Save & Update"
                                disabled={isRolesDisabled}
                                onClick={handleSubmit}
                                loadingSize={22}
                                loadingText="PROCESSING"
                            />
                        </DialogActions>
                    </>
                )}
            </Formik>
        </Dialog>
    );
};

const mapStateToProps = (state: any): any => ({
    profileState: state.profileState,
});

const mapDispatchToProps = (): any => ({});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ManageAccessEditDialog);
